function decimalDigitsOf(input: string): { digits: string; scale: number } | null {
  const match = input.trim().match(/^(-?)(\d*)(?:[.,](\d*))?$/);
  if (!match) return null;
  const [, sign, whole = '', fraction = ''] = match;
  if (whole.length === 0 && fraction.length === 0) return null;
  const digits = `${whole}${fraction}`.replace(/^0+(?=\d)/, '');
  return { digits: `${sign}${digits}`, scale: fraction.length };
}

export function parseDecimalToAmountScale(
  input: string,
): { amount: number; scale: number } | null {
  const parsed = decimalDigitsOf(input);
  if (parsed == null) return null;
  const amount = Number(parsed.digits);
  if (!Number.isSafeInteger(amount)) return null;
  return { amount, scale: parsed.scale };
}

/**
 * The backend's `percent_string` — `"42.13%"` — read as the exact pair `4213` at scale 2.
 *
 * A percent is a display string the client never recomputes, so this reads it rather than
 * deriving it: `parseFloat` would answer with a float for a figure the platform keeps as
 * an integer everywhere else, and comparing the string itself ranks `"9.5%"` above
 * `"42.13%"`. The pair is a `bigint` because a weight divides by a NAV and the backend
 * bounds neither: a near-zero NAV produces a percent with more digits than a JavaScript
 * number holds exactly, and that percent is still printed to the reader in full. A number
 * the reader can see must stay a number the client can rank, so the only `null` here is a
 * weight that is absent or is not a number at all.
 *
 * Shared by the positions sort and the allocation donut, which read the same field of the
 * same response and must not disagree about what it says.
 */
export function parsePercentToAmountScale(
  percent: string | null | undefined,
): { amount: bigint; scale: number } | null {
  if (percent === null || percent === undefined) return null;
  const parsed = decimalDigitsOf(percent.replace('%', ''));
  if (parsed == null) return null;
  return { amount: BigInt(parsed.digits), scale: parsed.scale };
}

function normalizedAmountScale(amount: number, scale: number): { amount: number; scale: number } {
  if (amount === 0) return { amount: 0, scale: 0 };
  let reducedAmount = amount;
  let reducedScale = scale;
  while (reducedScale > 0 && reducedAmount % 10 === 0) {
    reducedAmount /= 10;
    reducedScale -= 1;
  }
  return { amount: reducedAmount, scale: reducedScale };
}

function rescaledAmountScale(
  amount: number,
  scale: number,
  targetScale: number,
): { amount: number; scale: number } | null {
  if (scale === targetScale) return { amount, scale };
  if (scale < targetScale) {
    const scaled = amount * 10 ** (targetScale - scale);
    if (!Number.isSafeInteger(scaled)) return null;
    return { amount: scaled, scale: targetScale };
  }
  const divisor = 10 ** (scale - targetScale);
  const absolute = Math.abs(amount);
  const whole = Math.floor(absolute / divisor);
  const remainder = absolute - whole * divisor;
  const rounded = remainder * 2 >= divisor ? whole + 1 : whole;
  if (!Number.isSafeInteger(rounded)) return null;
  return { amount: amount < 0 ? -rounded : rounded, scale: targetScale };
}

/**
 * Multiply two amount/scale pairs and express the product at `targetScale`.
 *
 * Both factors are normalized to their own significant precision before the multiply.
 * Composing at `scaleA + scaleB` instead makes the product's magnitude depend on how many
 * trailing zeros the wire happened to carry: a scale-8 quantity of `4.00000000` at a
 * 2-decimal price overflows `Number.MAX_SAFE_INTEGER` and reports a trivially
 * representable value as unrepresentable. Normalizing first removes that dependency —
 * only the digits that carry information reach the multiply.
 *
 * The product is then rounded, half away from zero, to exactly `targetScale`, which the
 * caller sets to the precision the value is meaningful at (for a trade's gross, the
 * price's own scale). The composed scale is an artifact of the multiplication, so a
 * quantity's trailing precision must not widen the money figure; and `325.00` must not
 * narrow to `325`. `null` is reserved for a product that genuinely cannot be represented.
 */
export function multiplyAmountScale(
  amountA: number | null | undefined,
  scaleA: number | null | undefined,
  amountB: number | null | undefined,
  scaleB: number | null | undefined,
  targetScale: number | null | undefined,
): { amount: number; scale: number } | null {
  if (amountA === null || amountA === undefined || amountB === null || amountB === undefined) {
    return null;
  }
  if (scaleA === null || scaleA === undefined || scaleB === null || scaleB === undefined) {
    return null;
  }
  if (targetScale === null || targetScale === undefined) return null;
  if (!Number.isSafeInteger(amountA) || !Number.isSafeInteger(amountB)) return null;
  if (!Number.isInteger(scaleA) || !Number.isInteger(scaleB) || scaleA < 0 || scaleB < 0) {
    return null;
  }
  if (!Number.isInteger(targetScale) || targetScale < 0) return null;
  const normalizedA = normalizedAmountScale(amountA, scaleA);
  const normalizedB = normalizedAmountScale(amountB, scaleB);
  const amount = normalizedA.amount * normalizedB.amount;
  if (!Number.isSafeInteger(amount)) return null;
  return rescaledAmountScale(amount, normalizedA.scale + normalizedB.scale, targetScale);
}

/**
 * Whether `formatAmountScale` will render this pair as a number rather than a dash.
 *
 * Callers that decide *whether to show a row at all* must ask this, not `!= null`: a
 * present-but-unrepresentable amount passes a null check and then renders as `—`, which
 * puts a dash where the reader expects a figure and states nothing about why. The
 * predicate lives here, beside the formatter it describes, so the two cannot drift into
 * disagreeing about what is renderable.
 */
export function isRenderableAmountScale(
  amount: number | null | undefined,
  scale: number | null | undefined,
): boolean {
  if (amount === null || amount === undefined || scale === null || scale === undefined) {
    return false;
  }
  return Number.isSafeInteger(amount) && Number.isInteger(scale) && scale >= 0;
}

export function formatAmountScale(
  amount: number | null | undefined,
  scale: number | null | undefined,
  options?: { trimZeros?: boolean },
): string {
  if (amount === null || amount === undefined || scale === null || scale === undefined) {
    return '—';
  }
  if (!isRenderableAmountScale(amount, scale)) {
    return '—';
  }
  const negative = amount < 0;
  let digits = Math.abs(amount).toString();
  if (scale > 0) {
    digits = digits.padStart(scale + 1, '0');
    const whole = digits.slice(0, digits.length - scale);
    let fraction = digits.slice(digits.length - scale);
    if (options?.trimZeros) {
      fraction = fraction.replace(/0+$/, '');
    }
    digits = fraction.length > 0 ? `${whole}.${fraction}` : whole;
  }
  return negative ? `-${digits}` : digits;
}
